/**
 * Tiny, total expression evaluator for figure specs.
 *
 * Model-authored strings reach this file, so there is deliberately no `eval`,
 * no `new Function`, and no property access of any kind: the tokenizer only
 * emits numbers, the variable `x`, a fixed operator set and a fixed function
 * table. Anything else fails to parse and the caller drops the series.
 */
export const MAX_EXPRESSION_LENGTH = 200;
const MAX_NODES = 200;
const FUNCTIONS = {
    sin: { arity: 1, fn: (a) => Math.sin(a) },
    cos: { arity: 1, fn: (a) => Math.cos(a) },
    tan: { arity: 1, fn: (a) => Math.tan(a) },
    asin: { arity: 1, fn: (a) => Math.asin(a) },
    acos: { arity: 1, fn: (a) => Math.acos(a) },
    atan: { arity: 1, fn: (a) => Math.atan(a) },
    sinh: { arity: 1, fn: (a) => Math.sinh(a) },
    cosh: { arity: 1, fn: (a) => Math.cosh(a) },
    tanh: { arity: 1, fn: (a) => Math.tanh(a) },
    abs: { arity: 1, fn: (a) => Math.abs(a) },
    sqrt: { arity: 1, fn: (a) => Math.sqrt(a) },
    cbrt: { arity: 1, fn: (a) => Math.cbrt(a) },
    exp: { arity: 1, fn: (a) => Math.exp(a) },
    ln: { arity: 1, fn: (a) => Math.log(a) },
    log: { arity: 1, fn: (a) => Math.log10(a) },
    log10: { arity: 1, fn: (a) => Math.log10(a) },
    log2: { arity: 1, fn: (a) => Math.log2(a) },
    floor: { arity: 1, fn: (a) => Math.floor(a) },
    ceil: { arity: 1, fn: (a) => Math.ceil(a) },
    round: { arity: 1, fn: (a) => Math.round(a) },
    sign: { arity: 1, fn: (a) => Math.sign(a) },
    min: { arity: 2, fn: (a, b) => Math.min(a, b) },
    max: { arity: 2, fn: (a, b) => Math.max(a, b) },
    pow: { arity: 2, fn: (a, b) => Math.pow(a, b) },
};
// Null-prototype so `hasOwnProperty` is the only lookup path: a plain object
// literal answers `'constructor' in CONSTANTS` with true, which would hand the
// evaluator the Object constructor as if it were a number.
const CONSTANTS = Object.assign(Object.create(null), {
    pi: Math.PI,
    e: Math.E,
});
function tokenize(input) {
    const tokens = [];
    let i = 0;
    while (i < input.length) {
        const ch = input[i];
        if (ch === ' ' || ch === '\t') {
            i += 1;
            continue;
        }
        if (ch >= '0' && ch <= '9') {
            let j = i;
            while (j < input.length && /[0-9]/.test(input[j]))
                j += 1;
            if (input[j] === '.') {
                j += 1;
                while (j < input.length && /[0-9]/.test(input[j]))
                    j += 1;
            }
            if (input[j] === 'e' || input[j] === 'E') {
                // Only treat `e` as an exponent when it is followed by digits, so that
                // `2e` stays `2 * e` (Euler's number) rather than a truncated literal.
                let k = j + 1;
                if (input[k] === '+' || input[k] === '-')
                    k += 1;
                if (k < input.length && /[0-9]/.test(input[k])) {
                    j = k;
                    while (j < input.length && /[0-9]/.test(input[j]))
                        j += 1;
                }
            }
            const v = Number(input.slice(i, j));
            if (!Number.isFinite(v))
                return null;
            tokens.push({ t: 'num', v });
            i = j;
            continue;
        }
        if (ch === '.') {
            let j = i + 1;
            while (j < input.length && /[0-9]/.test(input[j]))
                j += 1;
            if (j === i + 1)
                return null;
            tokens.push({ t: 'num', v: Number(input.slice(i, j)) });
            i = j;
            continue;
        }
        if (/[a-zA-Z_]/.test(ch)) {
            let j = i;
            while (j < input.length && /[a-zA-Z_0-9]/.test(input[j]))
                j += 1;
            tokens.push({ t: 'name', v: input.slice(i, j).toLowerCase() });
            i = j;
            continue;
        }
        if ('+-*/^(),'.includes(ch)) {
            tokens.push({ t: 'op', v: ch });
            i += 1;
            continue;
        }
        return null;
    }
    return tokens;
}
/** Recursive-descent parser. Returns null on anything it does not recognise. */
function parse(tokens) {
    let pos = 0;
    let nodes = 0;
    const budget = (node) => {
        nodes += 1;
        return nodes > MAX_NODES ? null : node;
    };
    const peek = () => tokens[pos];
    const eatOp = (v) => {
        const tok = peek();
        if (tok && tok.t === 'op' && tok.v === v) {
            pos += 1;
            return true;
        }
        return false;
    };
    function parseExpression() {
        let left = parseTerm();
        if (!left)
            return null;
        for (;;) {
            const tok = peek();
            if (!tok || tok.t !== 'op' || (tok.v !== '+' && tok.v !== '-'))
                return left;
            pos += 1;
            const right = parseTerm();
            if (!right)
                return null;
            left = budget({ t: 'bin', op: tok.v, a: left, b: right });
            if (!left)
                return null;
        }
    }
    function parseTerm() {
        let left = parseUnary();
        if (!left)
            return null;
        for (;;) {
            const tok = peek();
            if (!tok)
                return left;
            // Implicit multiplication: models write `2x`, `3sin(x)`, `2(x+1)` far more
            // often than the explicit form, and rejecting those loses whole figures.
            const implicit = tok.t === 'num' || tok.t === 'name' || (tok.t === 'op' && tok.v === '(');
            if (!implicit && (tok.t !== 'op' || (tok.v !== '*' && tok.v !== '/')))
                return left;
            if (!implicit)
                pos += 1;
            const right = parseUnary();
            if (!right)
                return null;
            const op = implicit ? '*' : tok.v;
            left = budget({ t: 'bin', op: op, a: left, b: right });
            if (!left)
                return null;
        }
    }
    function parseUnary() {
        if (eatOp('-')) {
            const inner = parseUnary();
            return inner ? budget({ t: 'neg', a: inner }) : null;
        }
        if (eatOp('+'))
            return parseUnary();
        return parsePower();
    }
    function parsePower() {
        const base = parseAtom();
        if (!base)
            return null;
        if (eatOp('^')) {
            // Right-associative, and the exponent may itself be signed: 2^-x.
            const exp = parseUnary();
            return exp ? budget({ t: 'bin', op: '^', a: base, b: exp }) : null;
        }
        return base;
    }
    function parseAtom() {
        const tok = peek();
        if (!tok)
            return null;
        if (tok.t === 'num') {
            pos += 1;
            return budget({ t: 'num', v: tok.v });
        }
        if (tok.t === 'op' && tok.v === '(') {
            pos += 1;
            const inner = parseExpression();
            if (!inner || !eatOp(')'))
                return null;
            return inner;
        }
        if (tok.t === 'name') {
            pos += 1;
            if (tok.v === 'x')
                return budget({ t: 'var' });
            if (Object.prototype.hasOwnProperty.call(CONSTANTS, tok.v)) {
                return budget({ t: 'num', v: CONSTANTS[tok.v] });
            }
            if (Object.prototype.hasOwnProperty.call(FUNCTIONS, tok.v)) {
                const fn = tok.v;
                if (!eatOp('('))
                    return null;
                const args = [];
                if (!eatOp(')')) {
                    for (;;) {
                        const arg = parseExpression();
                        if (!arg)
                            return null;
                        args.push(arg);
                        if (eatOp(','))
                            continue;
                        if (eatOp(')'))
                            break;
                        return null;
                    }
                }
                if (args.length !== FUNCTIONS[fn].arity)
                    return null;
                return budget({ t: 'call', fn, args });
            }
            return null;
        }
        return null;
    }
    const ast = parseExpression();
    if (!ast || pos !== tokens.length)
        return null;
    return ast;
}
function evaluate(node, x) {
    switch (node.t) {
        case 'num':
            return node.v;
        case 'var':
            return x;
        case 'neg':
            return -evaluate(node.a, x);
        case 'bin': {
            const a = evaluate(node.a, x);
            const b = evaluate(node.b, x);
            switch (node.op) {
                case '+':
                    return a + b;
                case '-':
                    return a - b;
                case '*':
                    return a * b;
                case '/':
                    return a / b;
                case '^':
                    return Math.pow(a, b);
                default:
                    return NaN;
            }
        }
        case 'call': {
            const spec = FUNCTIONS[node.fn];
            const args = node.args.map((arg) => evaluate(arg, x));
            return spec.fn(...args);
        }
        default:
            return NaN;
    }
}
/**
 * Compile an expression in `x`. Returns null when the string uses anything
 * outside the allow-list, so callers can drop the series instead of drawing
 * something wrong.
 */
export function compileExpression(source) {
    if (typeof source !== 'string')
        return null;
    const trimmed = source.trim();
    if (!trimmed || trimmed.length > MAX_EXPRESSION_LENGTH)
        return null;
    const tokens = tokenize(trimmed);
    if (!tokens || tokens.length === 0)
        return null;
    const ast = parse(tokens);
    if (!ast)
        return null;
    return (x) => {
        const value = evaluate(ast, x);
        return typeof value === 'number' ? value : NaN;
    };
}
/** True when `source` is something compileExpression can handle. */
export function isSupportedExpression(source) {
    return compileExpression(source) !== null;
}
